// Wizard configuration types for the new unified wizard system

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
  type: "vueform" | "custom" | "connection";
  order: number;
  isRequired: boolean;

  // VueForms-based step configuration
  vueformConfig?: VueFormConfig;

  // Custom pre-built step configuration
  customStepConfig?: CustomStepConfig;

  // Connection step configuration
  connectionConfig?: ConnectionStepConfig;

  // Validation rules
  validation?: WizardStepValidation;

  // Conditional display logic
  showIf?: WizardCondition[];
}

export interface VueFormConfig {
  schema: Record<string, any>; // Vueform schema object
  defaultValues?: Record<string, any>;
  size?: "sm" | "md" | "lg";
  displayErrors?: boolean;
}

export interface CustomStepConfig {
  componentName: string; // e.g., 'DataMappingTool', 'ApprovalFlow', 'CustomWizardStep'
  componentPath?: string; // Optional custom component path
  props?: Record<string, any>; // Props to pass to the custom component
  dataModel?: Record<string, any>; // Expected data structure
  toolbarActions?: CustomToolbarAction[]; // Custom toolbar buttons/actions
  helpContent?: string; // Help text or documentation
}

export interface ConnectionStepConfig {
  connectionTypes: string[]; // e.g., ['email', 'database', 'api']
  requiredConnections: string[];
  optionalConnections: string[];
  allowMultiple: boolean;
}

export interface WizardStepValidation {
  rules: ValidationRule[];
  customValidator?: string; // Reference to custom validation function
}

export interface ValidationRule {
  field: string;
  type: "required" | "email" | "url" | "custom";
  message: string;
  customRule?: string;
}

export interface CustomToolbarAction {
  id: string;
  label: string;
  icon?: string;
  action: string; // Function name or action type
  variant?: "primary" | "secondary" | "success" | "warning" | "danger";
  position?: "left" | "right";
}

export interface WizardCondition {
  field: string;
  operator: "equals" | "not_equals" | "contains" | "greater_than" | "less_than";
  value: any;
  logicalOperator?: "and" | "or";
}

// Workflow-level wizard configuration
export interface WorkflowWizardConfig {
  workflowId: string;
  workflowName: string;
  version: string;
  steps: WizardStep[];
  metadata: {
    category: string;
    tags: string[];
    author: string;
    createdAt: string;
    updatedAt: string;
  };
}

// Product-level wizard configuration (combines connections and workflows)
export interface ProductWizardConfig {
  productId: string;
  productName: string;
  version: string;

  // Connection requirements (always come first in wizard flow)
  connectionRequirements: ProductConnectionRequirement[];

  // Workflow-specific step sequences (ordered after connections)
  workflowSequences: WorkflowSequence[];

  // Display configuration for the wizard
  displayConfig: {
    showConnectionsFirst: true; // Always true - connections must be configured first
    allowWorkflowReordering: boolean; // Can reorder workflows but not connections
    theme?: "default" | "compact" | "detailed";
  };

  metadata: {
    description: string;
    category: string;
    author: string;
    createdAt: string;
    updatedAt: string;
  };
}

export interface WorkflowSequence {
  workflowId: string;
  workflowName: string;
  order: number;
  steps: WizardStep[];
  isOptional: boolean;
}

export interface ProductConnectionRequirement {
  id: string;
  name: string;
  type: string;
  description: string;
  isRequired: boolean;
  usedByWorkflows: string[]; // Array of workflow IDs
  defaultConfig?: Record<string, any>;
}

// Project-level wizard runtime data
export interface ProjectWizardSession {
  projectId: string;
  productId: string;
  currentStep: number;
  totalSteps: number;
  completedSteps: string[];

  // User-provided data for each step
  stepData: Record<string, any>;

  // Connection configurations
  connectionData: Record<string, any>;

  // Session metadata
  sessionId: string;
  startedAt: string;
  lastUpdatedAt: string;
  isCompleted: boolean;
}

// Wizard runtime state management
export interface WizardState {
  isActive: boolean;
  currentSession: ProjectWizardSession | null;
  availableProducts: ProductWizardConfig[];
  loadingState: "idle" | "loading" | "saving" | "error";
  validationErrors: Record<string, string[]>;
}

// API interfaces for wizard management
export interface CreateWizardRequest {
  type: "workflow" | "product";
  workflowId?: string;
  productId?: string;
  config: WorkflowWizardConfig | ProductWizardConfig;
}

export interface UpdateWizardRequest extends CreateWizardRequest {
  id: string;
}

export interface WizardExecutionRequest {
  projectId: string;
  productId: string;
  stepData: Record<string, any>;
  connectionData: Record<string, any>;
}

export interface WizardExecutionResponse {
  success: boolean;
  projectId: string;
  configuredConnections: string[];
  errors?: string[];
  warnings?: string[];
}

// Publisher wizard designer interfaces
export interface WizardDesignerState {
  activeStep: WizardStep | null;
  draggedStep: WizardStep | null;
  previewMode: boolean;
  selectedStepType: "vueform" | "custom" | "connection";
}

export interface StepTemplate {
  id: string;
  name: string;
  description: string;
  icon: string;
  type: "vueform" | "custom" | "connection";
  template: Partial<WizardStep>;
}

// Connection templates (always appear first in wizard)
export const CONNECTION_TEMPLATES: StepTemplate[] = [
  {
    id: "email-smtp",
    name: "SMTP Email Service",
    description: "Configure SMTP server for sending emails",
    icon: "bi-envelope-at",
    type: "connection",
    template: {
      type: "connection",
      connectionConfig: {
        connectionTypes: ["email", "smtp"],
        requiredConnections: ["smtp-server"],
        optionalConnections: ["backup-smtp"],
        allowMultiple: false,
      },
    },
  },
  {
    id: "email-api",
    name: "Email API Service",
    description: "Configure email API (SendGrid, Mailchimp, etc.)",
    icon: "bi-envelope-plus",
    type: "connection",
    template: {
      type: "connection",
      connectionConfig: {
        connectionTypes: ["email", "api"],
        requiredConnections: ["email-api"],
        optionalConnections: [],
        allowMultiple: false,
      },
    },
  },
  {
    id: "database-sql",
    name: "SQL Database",
    description: "Configure SQL database connection (PostgreSQL, MySQL, etc.)",
    icon: "bi-database",
    type: "connection",
    template: {
      type: "connection",
      connectionConfig: {
        connectionTypes: ["database", "sql"],
        requiredConnections: ["primary-db"],
        optionalConnections: ["read-replica", "analytics-db"],
        allowMultiple: true,
      },
    },
  },
  {
    id: "api-integration",
    name: "REST API Integration",
    description: "Configure external REST API connections",
    icon: "bi-plugin",
    type: "connection",
    template: {
      type: "connection",
      connectionConfig: {
        connectionTypes: ["api", "rest", "webhook"],
        requiredConnections: [],
        optionalConnections: ["primary-api", "backup-api"],
        allowMultiple: true,
      },
    },
  },
  {
    id: "file-storage",
    name: "File Storage Service",
    description: "Configure file storage (AWS S3, Azure Blob, etc.)",
    icon: "bi-cloud-arrow-up",
    type: "connection",
    template: {
      type: "connection",
      connectionConfig: {
        connectionTypes: ["storage", "cloud", "files"],
        requiredConnections: ["file-storage"],
        optionalConnections: ["backup-storage"],
        allowMultiple: false,
      },
    },
  },
];

// VueForms templates
export const VUEFORM_TEMPLATES: StepTemplate[] = [
  {
    id: "user-onboarding-form",
    name: "User Onboarding Form",
    description: "Collect new user information during signup",
    icon: "bi-person-plus",
    type: "vueform",
    template: {
      type: "vueform",
      vueformConfig: {
        schema: {
          firstName: { type: "text", label: "First Name", rules: "required" },
          lastName: { type: "text", label: "Last Name", rules: "required" },
          email: {
            type: "text",
            inputType: "email",
            label: "Email Address",
            rules: "required|email",
          },
          company: { type: "text", label: "Company", rules: "" },
          role: {
            type: "select",
            label: "Role",
            items: ["Developer", "Manager", "Admin"],
            rules: "required",
          },
          preferences: {
            type: "checkbox",
            label: "Communication Preferences",
            items: ["Email Updates", "SMS Notifications", "Marketing"],
          },
        },
      },
    },
  },
  {
    id: "project-setup-form",
    name: "Project Setup Form",
    description: "Configure project-specific settings and preferences",
    icon: "bi-gear",
    type: "vueform",
    template: {
      type: "vueform",
      vueformConfig: {
        schema: {
          projectName: {
            type: "text",
            label: "Project Name",
            rules: "required",
          },
          description: { type: "textarea", label: "Project Description" },
          environment: {
            type: "radio",
            label: "Environment",
            items: ["Development", "Staging", "Production"],
            rules: "required",
          },
          timezone: {
            type: "select",
            label: "Timezone",
            items: ["UTC", "EST", "PST", "GMT"],
            rules: "required",
          },
          enableLogging: { type: "checkbox", label: "Enable Detailed Logging" },
          webhookUrl: {
            type: "text",
            inputType: "url",
            label: "Webhook URL (Optional)",
            rules: "url",
          },
        },
      },
    },
  },
  {
    id: "email-template-config",
    name: "Email Template Configuration",
    description: "Configure email templates and content settings",
    icon: "bi-envelope-paper",
    type: "vueform",
    template: {
      type: "vueform",
      vueformConfig: {
        schema: {
          welcomeSubject: {
            type: "text",
            label: "Welcome Email Subject",
            rules: "required",
          },
          welcomeTemplate: {
            type: "textarea",
            label: "Welcome Email Template",
            rules: "required",
          },
          fromName: { type: "text", label: "From Name", rules: "required" },
          fromEmail: {
            type: "text",
            inputType: "email",
            label: "From Email Address",
            rules: "required|email",
          },
          replyToEmail: {
            type: "text",
            inputType: "email",
            label: "Reply-To Email",
            rules: "email",
          },
          enableTracking: { type: "checkbox", label: "Enable Email Tracking" },
          unsubscribeLink: {
            type: "text",
            inputType: "url",
            label: "Unsubscribe Link",
            rules: "url",
          },
        },
      },
    },
  },
  {
    id: "notification-settings",
    name: "Notification Settings",
    description: "Configure notification preferences and channels",
    icon: "bi-bell",
    type: "vueform",
    template: {
      type: "vueform",
      vueformConfig: {
        schema: {
          enableEmailNotifications: {
            type: "checkbox",
            label: "Enable Email Notifications",
          },
          enableSlackNotifications: {
            type: "checkbox",
            label: "Enable Slack Notifications",
          },
          slackWebhook: {
            type: "text",
            inputType: "url",
            label: "Slack Webhook URL",
            rules: "url",
          },
          notificationFrequency: {
            type: "select",
            label: "Notification Frequency",
            items: ["Immediate", "Hourly", "Daily", "Weekly"],
            rules: "required",
          },
          criticalOnly: { type: "checkbox", label: "Critical Alerts Only" },
          alertThreshold: {
            type: "text",
            inputType: "number",
            label: "Error Threshold (count)",
            rules: "numeric|min:1",
          },
        },
      },
    },
  },
  {
    id: "data-mapping",
    name: "Data Mapping Configuration",
    description: "Map data fields between systems and workflows",
    icon: "bi-shuffle",
    type: "vueform",
    template: {
      type: "vueform",
      vueformConfig: {
        schema: {
          sourceField: {
            type: "select",
            label: "Source Field",
            items: ["user.email", "user.name", "user.id", "custom.field1"],
            rules: "required",
          },
          targetField: {
            type: "text",
            label: "Target Field Name",
            rules: "required",
          },
          transformation: {
            type: "select",
            label: "Data Transformation",
            items: [
              "None",
              "Uppercase",
              "Lowercase",
              "Remove Spaces",
              "Custom",
            ],
          },
          defaultValue: { type: "text", label: "Default Value (if empty)" },
          isRequired: { type: "checkbox", label: "Required Field" },
          validateFormat: {
            type: "select",
            label: "Format Validation",
            items: ["None", "Email", "Phone", "URL", "Custom Regex"],
          },
        },
      },
    },
  },
  {
    id: "approval-workflow",
    name: "Approval Workflow Setup",
    description: "Configure approval processes and escalation rules",
    icon: "bi-check-circle",
    type: "vueform",
    template: {
      type: "vueform",
      vueformConfig: {
        schema: {
          approverEmail: {
            type: "text",
            inputType: "email",
            label: "Primary Approver Email",
            rules: "required|email",
          },
          backupApproverEmail: {
            type: "text",
            inputType: "email",
            label: "Backup Approver Email",
            rules: "email",
          },
          autoApproveAmount: {
            type: "text",
            inputType: "number",
            label: "Auto-Approve Below Amount",
            rules: "numeric|min:0",
          },
          escalationDays: {
            type: "text",
            inputType: "number",
            label: "Escalation After (days)",
            rules: "required|numeric|min:1",
          },
          enableEmailReminders: {
            type: "checkbox",
            label: "Send Email Reminders",
          },
          reminderInterval: {
            type: "select",
            label: "Reminder Frequency",
            items: ["Daily", "Every 2 Days", "Weekly"],
            rules: "required",
          },
        },
      },
    },
  },
];

// Custom pre-built step templates
export const CUSTOM_STEP_TEMPLATES: StepTemplate[] = [
  {
    id: "data-mapping-tool",
    name: "Data Mapping Tool",
    description: "Interactive tool for mapping data fields between systems",
    icon: "bi-shuffle",
    type: "custom",
    template: {
      type: "custom",
      customStepConfig: {
        componentName: "DataMappingTool",
        props: {
          allowMultipleMappings: true,
          showPreview: true,
          enableValidation: true,
        },
        toolbarActions: [
          {
            id: "preview",
            label: "Preview Mapping",
            icon: "bi-eye",
            action: "showPreview",
            variant: "primary",
          },
          {
            id: "validate",
            label: "Validate",
            icon: "bi-check-circle",
            action: "validateMapping",
            variant: "success",
          },
          {
            id: "reset",
            label: "Reset",
            icon: "bi-arrow-clockwise",
            action: "resetMapping",
            variant: "secondary",
          },
        ],
      },
    },
  },
  {
    id: "approval-workflow-designer",
    name: "Approval Workflow Designer",
    description: "Visual designer for creating approval workflows",
    icon: "bi-diagram-3",
    type: "custom",
    template: {
      type: "custom",
      customStepConfig: {
        componentName: "ApprovalWorkflowDesigner",
        props: {
          maxApprovalLevels: 5,
          allowParallelApproval: true,
          enableEscalation: true,
        },
        toolbarActions: [
          {
            id: "add-step",
            label: "Add Approval Step",
            icon: "bi-plus-circle",
            action: "addApprovalStep",
            variant: "primary",
          },
          {
            id: "test-flow",
            label: "Test Workflow",
            icon: "bi-play-circle",
            action: "testWorkflow",
            variant: "success",
          },
        ],
      },
    },
  },
  {
    id: "template-editor",
    name: "Template Editor",
    description: "Rich editor for email/document templates with variables",
    icon: "bi-file-earmark-text",
    type: "custom",
    template: {
      type: "custom",
      customStepConfig: {
        componentName: "TemplateEditor",
        props: {
          templateType: "email",
          allowVariables: true,
          showPreview: true,
        },
        toolbarActions: [
          {
            id: "insert-variable",
            label: "Insert Variable",
            icon: "bi-braces",
            action: "showVariablePicker",
            variant: "primary",
          },
          {
            id: "preview",
            label: "Preview",
            icon: "bi-eye",
            action: "showTemplatePreview",
            variant: "secondary",
          },
        ],
      },
    },
  },
  {
    id: "schedule-builder",
    name: "Schedule Builder",
    description: "Visual cron-like schedule builder with preview",
    icon: "bi-calendar-event",
    type: "custom",
    template: {
      type: "custom",
      customStepConfig: {
        componentName: "ScheduleBuilder",
        props: {
          allowComplexSchedules: true,
          showNextRuns: 5,
          timezone: "UTC",
        },
        toolbarActions: [
          {
            id: "preview-runs",
            label: "Preview Next Runs",
            icon: "bi-calendar-check",
            action: "showNextRuns",
            variant: "primary",
          },
          {
            id: "validate-schedule",
            label: "Validate",
            icon: "bi-check-circle",
            action: "validateSchedule",
            variant: "success",
          },
        ],
      },
    },
  },
];

// Combined templates - connections always first, then vueforms and custom steps
export const COMMON_STEP_TEMPLATES: StepTemplate[] = [
  ...CONNECTION_TEMPLATES,
  ...VUEFORM_TEMPLATES,
  ...CUSTOM_STEP_TEMPLATES,
];
